/**
 * Dial input component
 * @author Carlos Lopez Rodriguez <clopez@adasasistemas.com>
 */

// Library
import React, { useMemo } from 'react';

import { TextField } from '@/components/general';
import { calculateMeterDialDigitsNumber, isAnalogMeterDial } from '@/utils';
// Components
import { OtpInput } from '../../general/otpInput/OtpInput';

// CSS Style for this module
import styles from './dialInput.module.scss';

/**
 * Interface for DialInput props
 */
interface DialInputProps {
	/**
	 * The value of the dial input as a concatenation of each
	 * character from every individual input value
	 */
	value: string | undefined;
	/**
	 * Dial type with patern 'm' followed by 2 digits (e.g. 'm34') to determine the decimal digits
	 */
	dialType?: string | null;
	/**
	 * onChange function for keeping track field value
	 * debounce is built in
	 */
	handleChange?: (value: string) => void;
	/**
	 * Msg to be displayed when the status is error
	 */
	errorMsg?: string;
	/**
	 * String for determining whether a error should be displayed
	 */
	status?: 'valid' | 'error';
}

/**
 * DialInput component is an extension of an otp input which lets users enter digits
 * individually in a set of inputs stylized as a meter's dial
 * @param {DialInputProps} Prop
 * @returns a meter's dial input
 */
export const DialInput = ({
	value,
	dialType,
	errorMsg = 'Please enter all of the digits for the reading',
	status = 'valid',
	handleChange = () => {
		return;
	},
}: DialInputProps) => {
	const isAnalogDial = useMemo(() => isAnalogMeterDial(dialType), [dialType]);
	const meterDialDigitsNumber = useMemo(() => calculateMeterDialDigitsNumber(dialType), [dialType]);
	const numberOfDigitsAfter = useMemo(
		() => (isAnalogDial ? Number(dialType?.charAt(2)) : 0),
		[isAnalogDial, dialType],
	);

	const variableDialPatternWithDecimals = '^[0-9.]{0,13}$';
	const variableDialPatternBWithoutDecimals = '^[0-9.]{0,12}$';

	/**
	 * This single pattern contains a maximum of 12 digits with an additional, optional decimal point.
	 * And does not let type an ending decimal point.
	 * As the pattern is applied on each change, this one would not work not let type a decimal point
	 */
	// const variableDialPattern = '^(?=.{0,13}$)\\d{0,12}(?:\\.\\d+)?$';

	return isAnalogDial ? (
		<OtpInput
			containerStyles={styles['dial-container']}
			inputStyles={styles.digit}
			specialInputStyles={styles['digit--decimal']}
			value={value}
			valueLength={meterDialDigitsNumber}
			specialInputsNumber={numberOfDigitsAfter}
			specialInputsSide="right"
			placeholder="0"
			onChange={(value: string) => {
				handleChange(value);
			}}
			errorMsg={errorMsg}
			status={status}
		/>
	) : (
		<TextField
			customStyles={styles['variable-dial-input']}
			type="number"
			inputMode="numeric"
			handleChange={e => handleChange(e.target.value)}
			defaultValue={value}
			pattern={
				value?.includes('.') ? variableDialPatternWithDecimals : variableDialPatternBWithoutDecimals
			}
			placeholder=""
			errorMsg={errorMsg}
			status={status}
		></TextField>
	);
};
